/**
 * computeFormDerives
 *
 * The engine's single evaluation entry point: one pure, synchronous pass turning
 * a validated derive config plus the documents it reads into exactly one result
 * per entry — a field value, a projected-row plan, or a skip. The server
 * reconciler persists those results and the React client displays them, so
 * neither can compute a different number.
 *
 * Three invariants carry the design. Missing required input THROWS rather than
 * degrading, because a skip is unstampable and a `null` deletes real data — a
 * caller bug must fail loudly, not mutate silently. Source rows are ALWAYS read
 * formula-fresh through `computeArRowFormulas`, never through the persisted
 * `calcInput` cache. And each entry's own output is layered over the in-memory
 * version fields and target table before any later entry reads them, which is
 * what makes a chained derive converge in one pass and makes the emitted hashes
 * a fixpoint against the state the pass itself will write. The caller's objects
 * are never mutated; the overlay lives on copies.
 *
 * A row projection's grain is the projected ROW: one plan row per distinct
 * canonical key value, carrying every declared column, and one fingerprint per
 * projected row covering that row's group alone.
 */

import { LexoRank } from 'lexorank';
import {
  applyArFormulaResultsToCells,
  computeArRowFormulas,
  computeColumnAggregation,
  evaluateFormula,
  readArStoredFormulaResult,
} from '../arTableFormulaEngine';
import { FORM_DERIVE_ROW_ID_PREFIX } from '../System.FormDerive.types';
import { canonicalizeFormDeriveValue } from './canonicalizeFormDeriveValue';
import { compareFormDeriveText } from './compareFormDeriveText';
import { computeFormDeriveFingerprint } from './computeFormDeriveFingerprint';
import { detectFormDeriveCycle } from './detectFormDeriveCycle';
import { formDeriveOperandPaths } from './formDeriveOperandPaths';
import { formDeriveProjectionColumnWrites } from './formDeriveProjectionColumnWrites';
import { formDeriveProjectionRowId } from './formDeriveProjectionRowId';
import { formDeriveValuesMatch } from './formDeriveValuesMatch';
import { isRecordValue } from './isRecordValue';
import { readFormDeriveCellValue } from './readFormDeriveCellValue';
import { readFormDeriveRecordProp } from './readFormDeriveRecordProp';
import { readFormDeriveReferenceDocId } from './readFormDeriveReferenceDocId';
import { resolveFormDeriveInputs } from './resolveFormDeriveInputs';
import { resolveFormDeriveNumericOperand } from './resolveFormDeriveNumericOperand';
import { resolveFormDeriveSourceRowSkip } from './resolveFormDeriveSourceRowSkip';
import { roundFormDeriveCents } from './roundFormDeriveCents';
import type { ArAggregationRow, ArFormulaEngineConfig } from '../arTableFormulaEngine';
import type {
  ArCell,
  ArCellValue,
  ArFormulaEntry,
  ArRowData,
  ArRowId,
  ArTableId,
  TypeId,
} from '../System.Artifacts.types';
import type {
  FormDeriveCaseCondition,
  FormDeriveCaseOperand,
  FormDeriveCasesSource,
  FormDeriveEntry,
  FormDeriveFieldTarget,
  FormDeriveId,
  FormDeriveProjectionColumn,
  FormDeriveReferencePropSource,
  FormDeriveRowOwner,
  FormDeriveRowReferencePropSource,
  FormDeriveRowValueSource,
  FormDeriveScalarSource,
  FormDeriveSkipReason,
  FormDeriveTableTarget,
} from '../System.FormDerive.types';
import type { EntityRecord, FieldId, FormVersion } from '../System.types';
import type { FormDeriveEngineConfig } from './createFormDeriveEngineConfig';

/** One row as the caller fetched it: Firestore docId plus raw document data. */
export type FormDeriveRow = { rowId: ArRowId; data: Partial<ArRowData> };

/**
 * One table's complete current state. `rows` is EVERY document of the rows
 * subcollection (aggregation row included — the engine filters); `formulaConfig`
 * is `createArFormulaEngineConfig(tableTypeDoc)` for that table.
 */
export type FormDeriveTableInput = {
  rows: FormDeriveRow[];
  formulaConfig: ArFormulaEngineConfig;
};

export type FormDeriveVersionInput = Partial<Pick<FormVersion, 'fields' | 'entityReferences'>>;

/** `null` = the caller looked and the record document does not exist. */
export type FormDeriveRecordInput = Pick<
  EntityRecord,
  'name' | 'fields' | 'entityReferences'
> | null;

export type ComputeFormDerivesInput = {
  config: FormDeriveEngineConfig;
  version: FormDeriveVersionInput;
  /** Keyed by ArTableId. Must contain every table any supplied entry sources OR targets. */
  tables: Record<ArTableId, FormDeriveTableInput>;
  /**
   * Keyed by entityRecordId. Must contain a key for every referenceProp source whose
   * referenceKey RESOLVES on `version.entityReferences` (an unresolved reference needs
   * no record and the engine will not consult this map for it), and for every docId a
   * projection's `rowReferenceProp` winner cell names. The fetch set and the `tables`
   * input must come from ONE row read: re-reading rows in between is what turns the
   * missing-record throw from a caller bug into a race.
   */
  records: Record<string, FormDeriveRecordInput>;
};

/** A field-target result. `value` is never `undefined`; `null` means delete the stored value. */
export type FormDeriveFieldResult = {
  kind: 'field';
  deriveId: FormDeriveId;
  target: FormDeriveFieldTarget;
  value: unknown;
  /** Sorted distinct canonical operand paths. Goes into the stamp verbatim. */
  inputs: string[];
  /** fnv1a64 of the canonical material, 16 hex chars. */
  hash: string;
};

/**
 * One projected row that must be written. Emitted ONLY when the stored row is
 * absent or differs — a row that exists and is already correct appears in
 * neither list.
 */
export type FormDeriveRowUpsert = {
  /** `drv~<deriveId>~<suffix>`. */
  rowId: ArRowId;
  /** True when no document exists at rowId; the writer sets createdAt/modifiedAt then, and only then. */
  create: boolean;
  /**
   * Desired value of every column the entry declares, keyed by the DECLARED
   * typeId — the dotted spelling preserved, because the dot is what sends the
   * write to the composite sub-path `readArStoredFormulaResult` reads rather
   * than to a flat key nothing reads. A blank projects as `null`, never as a
   * missing member: the row exists because of the key, so blanking a cell is
   * honest where dropping the column is not.
   */
  values: Record<TypeId, ArCellValue>;
  deriveOwner: FormDeriveRowOwner;
  /** Present = write it. Minted on create alone; a surviving row's order is the user's. */
  order?: string;
};

export type FormDeriveProjectionResult = {
  kind: 'projection';
  deriveId: FormDeriveId;
  target: FormDeriveTableTarget;
  /** Sorted ascending by rowId (plain `<`). */
  upserts: FormDeriveRowUpsert[];
  /**
   * Whole-document deletes, sorted ascending. Every member's id begins
   * `drv~<deriveId>~` — the id namespace is the sole delete authority, because
   * projected rows carry no rules protection and `deriveOwner` is therefore
   * client-forgeable. A forged marker outside the namespace is never honored here.
   */
  deletes: ArRowId[];
  inputs: string[];
  /**
   * One fingerprint per PROJECTED row, keyed by rowId — every projected row, not
   * only the upserted ones, because the key set doubles as the membership
   * record: a group appearing or vanishing moves the map even when every
   * surviving hash matches. Each hash covers its own group alone, so a row
   * rehashes only when that group moves.
   */
  rowHashes: Record<ArRowId, string>;
};

export type FormDeriveSkipResult = {
  kind: 'skipped';
  deriveId: FormDeriveId;
  reason: FormDeriveSkipReason;
};

export type FormDeriveResult =
  | FormDeriveFieldResult
  | FormDeriveProjectionResult
  | FormDeriveSkipResult;

/** An entry that projects rows: the one arm declaring `columns`. */
type FormDeriveProjectionEntry = Extract<
  FormDeriveEntry,
  { columns: FormDeriveProjectionColumn[] }
>;

/** An entry combining scalar operands algebraically. */
type FormDeriveAlgebraEntry = Extract<FormDeriveEntry, { expression: string }>;

/** The engine's private copy of a table: mutated by the overlay, never the caller's. */
type WorkingTable = {
  rows: Map<ArRowId, Partial<ArRowData>>;
  formulaConfig: ArFormulaEngineConfig;
};

/** A source row that participates, with its formula-fresh reads already done once. */
type ParticipatingRow = {
  rowId: ArRowId;
  data: Partial<ArRowData>;
  cells: Record<TypeId, ArCell>;
  /** Undefined exactly when the formula engine skipped the row; stored values then win. */
  formulaResults: ReadonlyMap<TypeId, number | null> | undefined;
};

/** Fingerprint lines keyed by the operand path they belong to, so paths dedupe. */
type OperandMaterial = Map<string, string[]>;

type ScalarRead = { value: unknown; material: OperandMaterial };

type RowValueRead = { value: ArCellValue; material: OperandMaterial };

/**
 * One projected row mid-fold: its group in rowId order, the group's winner, and
 * the aliases EARLIER columns of the same entry have bound. The alias map is the
 * fold's one working copy — a `cases` guard reads it, no other arm does.
 */
type ProjectionRowFrame = {
  group: ParticipatingRow[];
  winner: ParticipatingRow;
  aliases: Map<string, unknown>;
};

/** A row the entry wants to exist, before it is diffed against what is stored. */
type DesiredRow = {
  rowId: ArRowId;
  values: Record<TypeId, ArCellValue>;
  deriveOwner: FormDeriveRowOwner;
  order?: string;
};

type EngineContext = {
  version: FormDeriveVersionInput;
  records: Record<string, FormDeriveRecordInput>;
  tables: Map<ArTableId, WorkingTable>;
  /** Field values written by earlier entries of this same pass. */
  fieldOverlay: Map<FieldId, unknown>;
};

function isProjectionEntry(entry: FormDeriveEntry): entry is FormDeriveProjectionEntry {
  return 'columns' in entry;
}

/**
 * A caller that did not fetch a table cannot be served: a skip is unstampable and
 * a `null` would delete every owned projected row.
 */
function requireTable(ctx: EngineContext, tableId: ArTableId, deriveId: FormDeriveId): WorkingTable {
  const table = ctx.tables.get(tableId);
  if (table === undefined) {
    throw new Error(
      `computeFormDerives: derive "${deriveId}" reads table "${tableId}", which is absent from the tables input`,
    );
  }
  return table;
}

function readFieldValue(ctx: EngineContext, fieldId: FieldId): unknown {
  return ctx.fieldOverlay.has(fieldId)
    ? ctx.fieldOverlay.get(fieldId)
    : ctx.version.fields?.[fieldId]?.value;
}

/**
 * The source population — exactly what `computeAggregations` folds over — sorted
 * by rowId, with ONE formula computation per row feeding every reader: `cells`
 * for the partition key and the column arms, `formulaResults` for the
 * aggregations.
 */
function participatingRows(table: WorkingTable): ParticipatingRow[] {
  return [...table.rows.entries()]
    .filter(([rowId, data]) => !resolveFormDeriveSourceRowSkip(rowId, data))
    .sort(([a], [b]) => compareFormDeriveText(a, b))
    .map(([rowId, data]) => {
      const computation = computeArRowFormulas(rowId, data, table.formulaConfig);
      return {
        rowId,
        data,
        cells: applyArFormulaResultsToCells(data.cells, computation),
        formulaResults: computation.skipped ? undefined : computation.results,
      };
    });
}

/**
 * Drops members whose value is `undefined`, at every depth, returning the SAME
 * object whenever there is nothing to drop — so a Timestamp, a reference or any
 * other class instance passes through by identity rather than being rebuilt as a
 * plain object.
 *
 * This is what the engine must emit, because it is what it will read back:
 * Firestore runs with `ignoreUndefinedProperties`, so an `undefined` member is
 * never persisted. A projected composite carrying one — `applyArFormulaResultsToCells`
 * writes `fields.<sub>.value = undefined` for a null formula result — would
 * otherwise be compared against a stored object that lost the key and rewritten
 * on every pass, forever, each write retriggering the reconciler.
 */
function withoutUndefinedMembers(value: unknown): unknown {
  if (Array.isArray(value)) {
    const members: unknown[] = value.map((item: unknown) => withoutUndefinedMembers(item));
    return members.every((item, index) => item === value[index]) ? value : members;
  }
  if (!isRecordValue(value)) return value;

  const kept = Object.entries(value).flatMap(([key, member]) =>
    member === undefined ? [] : [[key, withoutUndefinedMembers(member)] as const],
  );
  const unchanged =
    kept.length === Object.keys(value).length &&
    kept.every(([key, member]) => member === value[key]);
  return unchanged ? value : Object.fromEntries(kept);
}

/**
 * The formula-fresh cell value of one participating row: `undefined` normalized
 * to `null`, and `undefined` MEMBERS of a composite dropped, so the emitted value
 * is byte-identical to what Firestore will hand back on the next pass. The cast
 * restates what the read already proved — stripping members changes no shape the
 * union names, and the stripper is total over `unknown` because a cell's value is
 * whatever Firestore stored, not only what the union declares. The address goes
 * through `readFormDeriveCellValue`, so a `sourceCell` may address a composite
 * sub-field and read back exactly where a dotted column writes.
 */
function effectiveCellValue(row: ParticipatingRow, typeId: TypeId): ArCellValue {
  return (
    (withoutUndefinedMembers(readFormDeriveCellValue(row.cells, typeId)) as ArCellValue) ?? null
  );
}

function cellCanon(row: ParticipatingRow, typeId: TypeId): string {
  return canonicalizeFormDeriveValue(readFormDeriveCellValue(row.cells, typeId)) ?? '';
}

/**
 * The stored `calcOutput` of one participating row's cell. `computeColumnAggregation`
 * PREFERS it over the cell value (`getNumericValue`), so an aggregate hash blind to
 * it reports fresh over a stale number and nothing repairs that until an unrelated
 * write lands — under-detection, the dangerous direction. Read through the
 * aggregation's own cell shape because `calcOutput` is a kit roll-up slot `ArCell`
 * does not declare, which is what stops the two readers from diverging. The
 * typeId is indexed WHOLE rather than read through `readFormDeriveCellValue`: only
 * `groupAggregate` and `colAggregate` consume this and both still refuse a dot, a
 * sub-field carries no `calcOutput` of its own, and root-collapsing here would
 * attribute the parent's roll-up to the sub-field.
 */
function calcOutputCanon(row: ParticipatingRow, typeId: TypeId): string {
  const cells: ArAggregationRow['cells'] = row.data.cells;
  return canonicalizeFormDeriveValue(cells?.[typeId]?.calcOutput) ?? '';
}

function mergeMaterial(parts: OperandMaterial[]): OperandMaterial {
  return new Map(parts.flatMap((part) => [...part]));
}

/** Sorted-path order, deduped per distinct path, joined with newlines. */
function joinMaterial(material: OperandMaterial): string {
  return [...material.keys()]
    .sort(compareFormDeriveText)
    .flatMap((path) => material.get(path) ?? [])
    .join('\n');
}

/**
 * A resolved reference whose key is absent from the records map is a caller bug
 * and throws: the caller built its fetch set from the same rows it passed in, so
 * a missing key is a broken contract, not a race.
 */
function requireRecord(
  ctx: EngineContext,
  entityRecordId: string,
  deriveId: FormDeriveId,
): FormDeriveRecordInput {
  if (!Object.prototype.hasOwnProperty.call(ctx.records, entityRecordId)) {
    throw new Error(
      `computeFormDerives: derive "${deriveId}" reads record "${entityRecordId}", which is absent from the records input`,
    );
  }
  return ctx.records[entityRecordId] ?? null;
}

/**
 * An unresolved reference needs no record: its material is the identity line
 * alone and its value is `null`. A resolved one the caller looked for and did not
 * find arrives as an explicit `null` and reads as `null`.
 */
function readReferencePropSource(
  ctx: EngineContext,
  source: FormDeriveReferencePropSource,
  paths: string[],
  deriveId: FormDeriveId,
): ScalarRead {
  const identityPath = paths[0] ?? '';
  const reference = ctx.version.entityReferences?.[source.referenceKey];
  const entityRecordId = reference?.entityRecordId;
  const identity: [string, string[]] = [
    identityPath,
    [`${identityPath}=${entityRecordId ?? ''}`],
  ];

  const recordPath = paths[1];
  if (entityRecordId === undefined || entityRecordId === '' || recordPath === undefined) {
    return { value: null, material: new Map([identity]) };
  }

  const record = requireRecord(ctx, entityRecordId, deriveId);
  const value = record === null ? null : (readFormDeriveRecordProp(record, source.prop) ?? null);
  return {
    value,
    material: new Map([
      identity,
      [recordPath, [`${recordPath}=${canonicalizeFormDeriveValue(value) ?? ''}`]],
    ]),
  };
}

/**
 * The three single-valued sources. `field` reads THROUGH the overlay, so a
 * chained derive sees what the earlier entry just computed and the hash line
 * records the value evaluation actually consumed.
 */
function readScalarSource(
  ctx: EngineContext,
  source: FormDeriveScalarSource,
  deriveId: FormDeriveId,
): ScalarRead {
  const paths = formDeriveOperandPaths(source, ctx.version.entityReferences);
  const path = paths[0] ?? '';

  if (source.kind === 'field') {
    const value = readFieldValue(ctx, source.fieldId);
    return {
      value,
      material: new Map([[path, [`${path}=${canonicalizeFormDeriveValue(value) ?? ''}`]]]),
    };
  }

  if (source.kind === 'colAggregate') {
    const table = requireTable(ctx, source.tableId, deriveId);
    const rows = participatingRows(table);
    const value = computeColumnAggregation(
      {
        typeId: source.typeId,
        aggregation: source.aggregation,
        aggregationFieldPath: source.aggregationFieldPath,
      },
      rows.map(({ data, formulaResults }) => ({ cells: data.cells, formulaResults })),
    );
    const lines = rows.map(
      (row) =>
        `${path}#${row.rowId}=${cellCanon(row, source.typeId)}|calc=${calcOutputCanon(row, source.typeId)}`,
    );
    return { value, material: new Map([[path, lines]]) };
  }

  return readReferencePropSource(ctx, source, paths, deriveId);
}

/**
 * Strict coercion, algebra only: the FIRST operand that fails to resolve makes
 * the whole entry yield `null`. Every operand's material is built regardless, so
 * repairing the offending value changes the hash and retriggers the entry.
 * `evaluateFormula` gets the GETTER form — a cells map would route through
 * `toNumeric`, turning garbage into a silent `0`.
 */
function computeAlgebraValue(
  ctx: EngineContext,
  entry: FormDeriveAlgebraEntry,
): { value: number | null; material: OperandMaterial } {
  const reads = entry.operands.map((operand) => ({
    alias: operand.alias,
    read: readScalarSource(ctx, operand.source, entry.deriveId),
  }));
  const material = mergeMaterial(reads.map(({ read }) => read.material));

  const resolved = reads.map(({ alias, read }) => ({
    alias,
    operand: resolveFormDeriveNumericOperand(read.value),
  }));
  const bindings: Record<string, number> = Object.fromEntries(
    resolved.flatMap(({ alias, operand }) => (operand.ok ? [[alias, operand.value]] : [])),
  );
  const value = resolved.some(({ operand }) => !operand.ok)
    ? null
    : evaluateFormula(entry.expression, (name: string) => bindings[name]);

  return { value, material };
}

/** `left` and an alias-shaped `right` read the environment; a literal reads itself. */
function readCaseOperand(
  operand: FormDeriveCaseOperand,
  aliases: ReadonlyMap<string, unknown>,
): unknown {
  return typeof operand === 'object' ? (aliases.get(operand.alias) ?? null) : operand;
}

/**
 * One ANDed guard. A `null` operand fails EVERY operator, `!=` included — a
 * missing input never satisfies a guard, and the authored default arm is the
 * explicit place for "no answer". That covers the alias an earlier column
 * evaluated to `null` and, harmlessly, the unbound alias a config defect would
 * have to leave behind: a defect drops to the default arm rather than taking
 * down the pass. Equality compares `canonicalizeFormDeriveValue` of both sides,
 * so strings and booleans need no coercion; an ordering operator requires two
 * FINITE NUMBERS and fails otherwise, because the derive path never routes an
 * operand through `toNumeric` — garbage means no answer, never a silent zero.
 */
function conditionHolds(
  condition: FormDeriveCaseCondition,
  aliases: ReadonlyMap<string, unknown>,
): boolean {
  const left = aliases.get(condition.left) ?? null;
  const right = readCaseOperand(condition.right, aliases);
  if (left === null || right === null) return false;

  if (condition.op === '==' || condition.op === '!=') {
    const equal = canonicalizeFormDeriveValue(left) === canonicalizeFormDeriveValue(right);
    return condition.op === '==' ? equal : !equal;
  }

  if (typeof left !== 'number' || !Number.isFinite(left)) return false;
  if (typeof right !== 'number' || !Number.isFinite(right)) return false;
  if (condition.op === '<') return left < right;
  if (condition.op === '<=') return left <= right;
  if (condition.op === '>') return left > right;
  return left >= right;
}

/** First case whose guards ALL hold; a `when`-less case matches unconditionally; no match is `null`. */
function readCasesValue(
  source: FormDeriveCasesSource,
  aliases: ReadonlyMap<string, unknown>,
): ArCellValue {
  const won = source.cases.find(
    ({ when }) =>
      when === undefined || when.every((condition) => conditionHolds(condition, aliases)),
  );
  return won === undefined ? null : won.value;
}

/**
 * The record behind the group's reference cell, read LIVE — never the cell's own
 * `fields`/`entityReferences` snapshot, which is a different feature. The
 * identity line is emitted whether or not a docId resolves, so re-pointing the
 * reference moves the row's hash even when the property reads `null` both times.
 * The cast is `effectiveCellValue`'s: a record property is whatever Firestore
 * stored, which the cell union only partly names, and stripping `undefined`
 * members changes no shape it does name.
 */
function readRowReferenceProp(
  ctx: EngineContext,
  source: FormDeriveRowReferencePropSource,
  tableId: ArTableId,
  winner: ParticipatingRow,
  deriveId: FormDeriveId,
): RowValueRead {
  const entityRecordId = readFormDeriveReferenceDocId(winner.cells, source.referenceTypeId);
  const paths = formDeriveOperandPaths(
    { ...source, tableId, entityRecordId },
    ctx.version.entityReferences,
  );
  const identityPath = paths[0] ?? '';
  const identity: [string, string[]] = [
    identityPath,
    [`${identityPath}#${winner.rowId}=${cellCanon(winner, source.referenceTypeId)}`],
  ];

  const recordPath = paths[1];
  if (entityRecordId === undefined || recordPath === undefined) {
    return { value: null, material: new Map([identity]) };
  }

  const record = requireRecord(ctx, entityRecordId, deriveId);
  const value =
    record === null
      ? null
      : ((withoutUndefinedMembers(readFormDeriveRecordProp(record, source.prop)) as ArCellValue) ?? null);
  return {
    value,
    material: new Map([
      identity,
      [recordPath, [`${recordPath}=${canonicalizeFormDeriveValue(value) ?? ''}`]],
    ]),
  };
}

/**
 * One declared column's value against one projected row's group, plus the
 * material that value is fingerprinted from. `groupKey` contributes no material:
 * the key's lines are emitted for every projected row unconditionally, and they
 * already carry the winner. `cases` contributes none either — its operands are
 * earlier columns whose material is already present, and the case list itself is
 * config the `rev` covers.
 */
function readRowValue(
  ctx: EngineContext,
  entry: FormDeriveProjectionEntry,
  source: FormDeriveRowValueSource,
  frame: ProjectionRowFrame,
): RowValueRead {
  const { tableId } = entry.source;

  if (source.kind === 'groupKey') {
    return { value: effectiveCellValue(frame.winner, entry.source.typeId), material: new Map() };
  }

  if (source.kind === 'cases') {
    return { value: readCasesValue(source, frame.aliases), material: new Map() };
  }

  if (source.kind === 'rowReferenceProp') {
    return readRowReferenceProp(ctx, source, tableId, frame.winner, entry.deriveId);
  }

  const path =
    formDeriveOperandPaths({ ...source, tableId }, ctx.version.entityReferences)[0] ?? '';

  if (source.kind === 'sourceCell') {
    return {
      value: effectiveCellValue(frame.winner, source.typeId),
      material: new Map([
        [path, [`${path}#${frame.winner.rowId}=${cellCanon(frame.winner, source.typeId)}`]],
      ]),
    };
  }

  const folded = computeColumnAggregation(
    {
      typeId: source.typeId,
      aggregation: source.aggregation,
      aggregationFieldPath: source.aggregationFieldPath,
    },
    frame.group.map(({ data, formulaResults }) => ({ cells: data.cells, formulaResults })),
  );
  const lines = frame.group.map(
    (member) =>
      `${path}#${member.rowId}=${cellCanon(member, source.typeId)}|calc=${calcOutputCanon(member, source.typeId)}`,
  );
  return {
    value: source.roundToCents === true ? roundFormDeriveCents(folded) : folded,
    material: new Map([[path, lines]]),
  };
}

function parseLexoRank(order: string): LexoRank | null {
  try {
    return LexoRank.parse(order);
  } catch {
    return null;
  }
}

/**
 * The highest order the target table already carries. Unparsable orders are
 * stepped over rather than thrown on: one corrupt row must not stop a form's
 * whole derive pass, and the next parsable rank still sits above every ordered
 * row the user can see.
 */
function highestParsableOrder(table: WorkingTable): LexoRank | null {
  return (
    [...table.rows.values()]
      .flatMap((data) => (typeof data.order === 'string' && data.order !== '' ? [data.order] : []))
      .sort(compareFormDeriveText)
      .reverse()
      .map(parseLexoRank)
      .find((rank): rank is LexoRank => rank !== null) ?? null
  );
}

/**
 * The next order strictly above `ceiling`, or `null` when there is none.
 *
 * `genNext()` cannot be trusted on its own: at `0|zzzzzz:` it returns the ceiling
 * UNCHANGED rather than throwing, so every row minted in a pass over a table
 * already sitting at the bucket maximum ties at one order. The guard compares the
 * minted string and, when the bucket is spent, hops to the next bucket's initial
 * rank — a strictly larger string (`1|y00000:` > `0|zzzzzz:`) with a full bucket
 * of room above it. Only the last bucket's maximum, `2|zzzzzz:`, has nothing
 * above it at all; the caller then mints no order rather than a tie, because a
 * user can drag an unordered row but cannot see one of two identical ranks.
 */
function orderAbove(ceiling: LexoRank): LexoRank | null {
  const next = ceiling.genNext();
  if (compareFormDeriveText(next.toString(), ceiling.toString()) > 0) return next;
  const hopped = LexoRank.initial(ceiling.getBucket().next());
  return compareFormDeriveText(hopped.toString(), ceiling.toString()) > 0 ? hopped : null;
}

/**
 * The partition: participating rows whose key cell canonicalizes alike, blanks
 * excluded. Members keep `rows`' rowId order, so a group's FIRST member is its
 * WINNER — the lowest rowId, a pure function of the row set with no iteration
 * order, arrival time or clock in it, which is why two concurrent reconcilers
 * compute an identical plan. `order` is read by no arm.
 */
function groupRowsByKey(
  rows: ParticipatingRow[],
  typeId: TypeId,
): Map<string, ParticipatingRow[]> {
  return rows.reduce((groups, row) => {
    const canon = canonicalizeFormDeriveValue(row.cells[typeId]?.value);
    if (canon === undefined) return groups;
    const members = groups.get(canon);
    if (members === undefined) return groups.set(canon, [row]);
    members.push(row);
    return groups;
  }, new Map<string, ParticipatingRow[]>());
}

/**
 * One projected row: the left-to-right fold over the entry's declared columns.
 * Each column's evaluated value binds to its alias for LATER columns only —
 * which is what the save-time earlier-only rule buys, and why a guard read needs
 * no node in the cycle graph. Every column is written whether or not a guard
 * reads it. The material always opens with one line per GROUP MEMBER of the
 * partition key, so membership is covered and a change of winner moves the hash
 * even when the canonical key value is identical.
 */
function computeProjectedRow(
  ctx: EngineContext,
  entry: FormDeriveProjectionEntry,
  canon: string,
  group: ParticipatingRow[],
  winner: ParticipatingRow,
): { desired: DesiredRow; hash: string } {
  const keyPath =
    formDeriveOperandPaths(entry.source, ctx.version.entityReferences)[0] ?? '';
  const keyMaterial: OperandMaterial = new Map([
    [keyPath, group.map((row) => `${keyPath}#${row.rowId}=${cellCanon(row, entry.source.typeId)}`)],
  ]);

  const frame: ProjectionRowFrame = { group, winner, aliases: new Map<string, unknown>() };
  const evaluated = entry.columns.map((column) => {
    const read = readRowValue(ctx, entry, column.value, frame);
    const value = read.value ?? null;
    if (column.alias !== undefined) frame.aliases.set(column.alias, value);
    return { typeId: column.typeId, value, material: read.material };
  });

  return {
    desired: {
      rowId: formDeriveProjectionRowId(entry.deriveId, { kind: 'unique', canonicalValue: canon }),
      values: Object.fromEntries(evaluated.map(({ typeId, value }) => [typeId, value])),
      deriveOwner: { deriveId: entry.deriveId, key: canon },
    },
    hash: computeFormDeriveFingerprint(
      joinMaterial(mergeMaterial([keyMaterial, ...evaluated.map(({ material }) => material)])),
    ),
  };
}

/** Rebuilds `target` with `value` at literal `segments`, copying every level it passes. */
function withValueAtSegments(
  target: Record<string, unknown>,
  segments: readonly string[],
  value: unknown,
): Record<string, unknown> {
  const [head, ...rest] = segments;
  const child = target[head];
  return rest.length === 0
    ? { ...target, [head]: value }
    : {
        ...target,
        [head]: withValueAtSegments(isRecordValue(child) ? child : {}, rest, value),
      };
}

/**
 * A row's cells with one declared column written, through the shared write rule
 * the reconciler also uses. The cast is `effectiveCellValue`'s in reverse: the
 * segment walk rebuilds only levels `ArCell` already declares.
 */
function withColumnValue(
  cells: Record<TypeId, ArCell>,
  typeId: TypeId,
  value: ArCellValue,
): Record<TypeId, ArCell> {
  const written = formDeriveProjectionColumnWrites(typeId, value).reduce(
    (carry, write) => withValueAtSegments(carry, write.segments, write.value),
    { cells } as Record<string, unknown>,
  );
  return written.cells as Record<TypeId, ArCell>;
}

/** What a declared column currently holds, read through the shape its writer used. */
function storedColumnValue(stored: Partial<ArRowData>, typeId: TypeId): unknown {
  return typeId.includes('.')
    ? readArStoredFormulaResult(stored.cells, typeId)
    : stored.cells?.[typeId]?.value;
}

/**
 * "Differs" in full, over EVERY declared column. `_calculated` and `deriveOwner`
 * are compared because an upsert repairs them: a row sitting at this derive's own
 * id with a missing or forged owner is claimed, since the id embeds the deriveId
 * and leaving it would block that projection slot forever. `order` is never
 * compared — it is minted on create alone and belongs to the user afterwards.
 */
function rowDiffers(desired: DesiredRow, stored: Partial<ArRowData>): boolean {
  const columnDiffers = Object.entries(desired.values).some(([typeId, value]) => {
    if (!formDeriveValuesMatch(value, storedColumnValue(stored, typeId))) return true;
    return !typeId.includes('.') && stored.cells?.[typeId]?._calculated !== true;
  });
  if (columnDiffers) return true;
  if (stored.deriveOwner?.deriveId !== desired.deriveOwner.deriveId) return true;
  return stored.deriveOwner?.key !== desired.deriveOwner.key;
}

/** Layers the plan onto the in-memory target table so later entries read it. */
function applyProjectionPlan(
  targetTable: WorkingTable,
  upserts: FormDeriveRowUpsert[],
  deletes: ArRowId[],
): void {
  for (const rowId of deletes) targetTable.rows.delete(rowId);
  for (const upsert of upserts) {
    const stored = targetTable.rows.get(upsert.rowId) ?? {};
    const cells = Object.entries(upsert.values).reduce(
      (next, [typeId, value]) => withColumnValue(next, typeId, value),
      stored.cells ?? {},
    );
    targetTable.rows.set(upsert.rowId, {
      ...stored,
      cells,
      deriveOwner: upsert.deriveOwner,
      ...(upsert.order === undefined ? {} : { order: upsert.order }),
    });
  }
}

/**
 * One row per distinct canonical key value, every declared column on each. Order
 * is minted only on create, chained above the target table's current maximum in
 * sorted-canon order; a surviving row's order belongs to the user from then on.
 */
function computeProjectionResult(
  ctx: EngineContext,
  entry: FormDeriveProjectionEntry,
): FormDeriveProjectionResult {
  const { deriveId, source, target } = entry;
  const sourceTable = requireTable(ctx, source.tableId, deriveId);
  const targetTable = requireTable(ctx, target.tableId, deriveId);
  const groups = groupRowsByKey(participatingRows(sourceTable), source.typeId);
  const ceiling = { rank: highestParsableOrder(targetTable) };

  const projected = [...groups.keys()].sort(compareFormDeriveText).flatMap((canon) => {
    const group = groups.get(canon) ?? [];
    const winner = group[0];
    if (winner === undefined) return [];
    const row = computeProjectedRow(ctx, entry, canon, group, winner);
    if (targetTable.rows.has(row.desired.rowId)) return [row];
    const minted = ceiling.rank === null ? LexoRank.middle() : orderAbove(ceiling.rank);
    if (minted === null) return [row];
    ceiling.rank = minted;
    return [{ ...row, desired: { ...row.desired, order: minted.toString() } }];
  });

  const desiredIds = new Set(projected.map(({ desired }) => desired.rowId));
  const upserts = projected
    .flatMap(({ desired }) => {
      const stored = targetTable.rows.get(desired.rowId);
      if (stored === undefined) return [{ ...desired, create: true }];
      return rowDiffers(desired, stored) ? [{ ...desired, create: false }] : [];
    })
    .sort((a, b) => compareFormDeriveText(a.rowId, b.rowId));

  const namespace = `${FORM_DERIVE_ROW_ID_PREFIX}${deriveId}~`;
  const deletes = [...targetTable.rows.keys()]
    .filter((rowId) => rowId.startsWith(namespace) && !desiredIds.has(rowId))
    .sort(compareFormDeriveText);

  applyProjectionPlan(targetTable, upserts, deletes);

  return {
    kind: 'projection',
    deriveId,
    target,
    upserts,
    deletes,
    inputs: resolveFormDeriveInputs(entry, ctx.version),
    rowHashes: Object.fromEntries(projected.map(({ desired, hash }) => [desired.rowId, hash])),
  };
}

/**
 * Field targets. The single-source shapes are NOT coerced: `field` and
 * `referenceProp` copy verbatim, and `colAggregate` passes its `number | null`
 * straight through, so an empty column is `null` — "no rows yet" as a standalone
 * answer is honestly "no answer", while the same aggregate used as an algebra
 * operand becomes `0` by the blank rule. Rounding is applied ONCE here, to the
 * final value, never per operand.
 */
function computeFieldResult(
  ctx: EngineContext,
  entry: Exclude<FormDeriveEntry, FormDeriveProjectionEntry>,
): FormDeriveFieldResult {
  const computed: { value: unknown; material: OperandMaterial } =
    'operands' in entry
      ? computeAlgebraValue(ctx, entry)
      : ((): { value: unknown; material: OperandMaterial } => {
          const read = readScalarSource(ctx, entry.source, entry.deriveId);
          return { value: read.value ?? null, material: read.material };
        })();

  const value =
    entry.roundToCents === true && (computed.value === null || typeof computed.value === 'number')
      ? roundFormDeriveCents(computed.value)
      : computed.value;

  ctx.fieldOverlay.set(entry.target.fieldId, value);

  return {
    kind: 'field',
    deriveId: entry.deriveId,
    target: entry.target,
    value,
    inputs: resolveFormDeriveInputs(entry, ctx.version),
    hash: computeFormDeriveFingerprint(joinMaterial(computed.material)),
  };
}

function computeFormDerives(input: ComputeFormDerivesInput): FormDeriveResult[] {
  const { config, version, tables, records } = input;

  const ctx: EngineContext = {
    version,
    records,
    tables: new Map(
      Object.entries(tables).map(([tableId, table]) => [
        tableId,
        {
          rows: new Map(table.rows.map(({ rowId, data }) => [rowId, data])),
          formulaConfig: table.formulaConfig,
        },
      ]),
    ),
    fieldOverlay: new Map<FieldId, unknown>(),
  };

  const tableFormulaSequences = new Map<ArTableId, ArFormulaEntry[]>(
    Object.entries(tables).map(([tableId, table]) => [
      tableId,
      table.formulaConfig.formulaSequence,
    ]),
  );
  const verdict = detectFormDeriveCycle({ entries: config.entries, tableFormulaSequences });

  const evaluated = verdict.order.map((entry) =>
    isProjectionEntry(entry) ? computeProjectionResult(ctx, entry) : computeFieldResult(ctx, entry),
  );

  const skipped = config.entries.flatMap<FormDeriveResult>((entry) => {
    if (verdict.formulaOwnedDeriveIds.has(entry.deriveId)) {
      return [{ kind: 'skipped', deriveId: entry.deriveId, reason: 'targetFormulaOwned' }];
    }
    return verdict.cyclicDeriveIds.has(entry.deriveId)
      ? [{ kind: 'skipped', deriveId: entry.deriveId, reason: 'cycle' }]
      : [];
  });

  return [...evaluated, ...skipped];
}

export { computeFormDerives };
